// User Data Service - User Data CRUD Operations with Encryption Support
// Extracted from userDataPersistence.js for better separation of concerns

import { debugLog, debugError, debugWarn } from '../debugUtils';
import { detectStorageEnvironment } from './storageDetection';
import {
  readFromStorage,
  writeToStorage,
  removeFromStorage,
  USER_DATA_KEY,
} from './storageHelpers';
import {
  getEncryptionKey,
  encryptValue,
  decryptValue,
} from './encryptionService';

// Performance cache to avoid repeated decryption
let decryptionCache = null;
let cacheTimestamp = 0;
const CACHE_DURATION = 30000; // 30 seconds

/**
 * Clear the performance cache
 * Useful for testing and when you need fresh data immediately
 */
export const clearPerformanceCache = () => {
  decryptionCache = null;
  cacheTimestamp = 0;
};

// Import shared email validation
import { validateEmail } from '../emailValidation';

/**
 * Validate email format using shared validation logic
 * Returns true/false for compatibility with existing allowlist logic
 */
const validateEmailFormat = email => !!validateEmail(email);

/**
 * Validate date format (YYYY-MM-DD)
 */
const validateDateFormat = date => {
  if (!date || typeof date !== 'string') return false;
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateRegex.test(date)) return false;

  // Check if it's a valid date
  const parsedDate = new Date(date);
  return (
    parsedDate instanceof Date &&
    !isNaN(parsedDate) &&
    date === parsedDate.toISOString().split('T')[0]
  );
};

/**
 * Validate quantity (1-10 reasonable limit)
 */
const validateQuantity = quantity => {
  const num = parseInt(quantity, 10);
  return !isNaN(num) && num >= 1 && num <= 10;
};

/**
 * URL Parameter Security Allowlist
 * SECURITY: Only these parameters are allowed from URLs
 * Customer IDs and premium state flags are NEVER trusted from URLs
 */
const SAFE_URL_PARAMS = {
  email: {
    dataKey: 'email',
    validator: validateEmailFormat,
    sanitizer: value => value.trim().toLowerCase(),
  },
  customer_email: {
    dataKey: 'email',
    validator: validateEmailFormat,
    sanitizer: value => value.trim().toLowerCase(),
  },
  selected_date: {
    dataKey: 'selectedDate',
    validator: validateDateFormat,
    sanitizer: value => value.trim(),
  },
  date: {
    dataKey: 'selectedDate',
    validator: validateDateFormat,
    sanitizer: value => value.trim(),
  },
  quantity: {
    dataKey: 'quantity',
    validator: validateQuantity,
    sanitizer: value => parseInt(value, 10),
  },
  qty: {
    dataKey: 'quantity',
    validator: validateQuantity,
    sanitizer: value => parseInt(value, 10),
  },
  // SECURITY NOTE: customer_id, customerId, has_reserved_seating, has_spotlight_pass
  // are INTENTIONALLY EXCLUDED for security reasons
};

/**
 * Extract user data from URL parameters (SECURITY HARDENED)
 */
export const extractFromUrlParams = urlSearchParams => {
  if (!urlSearchParams) return {};

  const params = new URLSearchParams(urlSearchParams);
  const urlData = {};

  // Only process allowlisted parameters
  Object.entries(SAFE_URL_PARAMS).forEach(([urlKey, config]) => {
    const value = params.get(urlKey);
    if (value) {
      // Validate the parameter
      if (config.validator(value)) {
        // Sanitize and store
        urlData[config.dataKey] = config.sanitizer(value);
      } else {
        // Rejected invalid URL parameter
      }
    }
  });

  // Log any rejected/suspicious parameters
  for (const [key, value] of params.entries()) {
    if (!SAFE_URL_PARAMS[key]) {
      // CRITICAL: Special warning for fbclid to prevent Meta attribution corruption
      if (key === 'fbclid') {
        debugWarn(
          `🚨 CRITICAL: fbclid parameter BLOCKED from URLSearchParams processing to prevent Meta attribution corruption! Use fbpTracking.js extractRawFbclid() instead.`
        );
      } else {
        debugLog(`🚨 Blocked untrusted URL parameter: ${key} = ${value}`);
      }
    }
  }

  return urlData;
};

/**
 * Validate and repair user data if possible
 * Handles edge cases like corrupted data or environment changes
 */
const validateAndRepairUserData = userData => {
  if (!userData || typeof userData !== 'object') {
    return null;
  }

  const repaired = {};
  let hasChanges = false;

  Object.keys(userData).forEach(key => {
    const value = userData[key];

    // Validate email format
    if (key === 'email') {
      if (
        typeof value === 'string' &&
        value.includes('@') &&
        value.length < 254
      ) {
        repaired[key] = value.trim().toLowerCase();
      } else {
        debugLog(`⚠️ Invalid email removed: ${value}`);
        hasChanges = true;
      }
    }
    // Validate customer ID format (encrypted or plain text)
    else if (key === 'stripeCustomerId') {
      if (
        typeof value === 'string' &&
        (value.startsWith('cus_') || value.startsWith('enc_'))
      ) {
        repaired[key] = value;
      } else {
        debugLog(`⚠️ Invalid customer ID removed: ${value}`);
        hasChanges = true;
      }
    }
    // Validate selectedDate format and availability
    else if (key === 'selectedDate') {
      if (
        typeof value === 'string' &&
        validateDateFormat(value)
      ) {
        // Note: We can't validate against available dates here since they're not available
        // This validation will be done in the hooks when available dates are loaded
        repaired[key] = value;
      } else {
        debugLog(`⚠️ Invalid selectedDate removed: ${value}`);
        hasChanges = true;
      }
    }
    // Keep all other valid values (truthy, false booleans, and 0 numbers)
    else if (value !== null && value !== undefined && value !== '') {
      repaired[key] = value;
    } else {
      hasChanges = true;
    }
  });

  if (hasChanges) {
    debugLog('🔧 User data was repaired during validation');
  }

  return Object.keys(repaired).length > 0 ? repaired : null;
};

/**
 * Store user data across all available storage mechanisms
 * SECURITY: Hybrid encryption strategy - encrypt where possible, plain text for OTO compatibility
 * @param {Object} userData - Data to store (flat object)
 */
export const storeUserData = async userData => {
  if (typeof window === 'undefined') return Promise.resolve();

  // Handle null/undefined input gracefully
  if (!userData || typeof userData !== 'object') {
    return Promise.resolve();
  }

  try {
    // Get existing data from the most reliable source
    const { value: existingDataStr } = readFromStorage(USER_DATA_KEY);
    const existingData = existingDataStr ? JSON.parse(existingDataStr) : {};

    // Merge new data with existing data
    const mergedData = { ...existingData, ...userData };

    // Clean the data before storing (remove empty/null/undefined values but keep false booleans)
    const cleanedData = {};
    Object.keys(mergedData).forEach(key => {
      const value = mergedData[key];
      // Keep all truthy values, false booleans, and 0 numbers - only filter out null, undefined, empty strings
      if (value !== null && value !== undefined && value !== '') {
        cleanedData[key] = value;
      }
    });

    // Apply encryption strategy based on environment
    const dataToStore = { ...cleanedData };
    const environment = detectStorageEnvironment();
    const encryptionKey = getEncryptionKey();

    if (dataToStore.stripeCustomerId) {
      // Apply encryption based on environment (simple approach)
      if (environment.strategy === 'ENCRYPTED' && encryptionKey) {
        // Only encrypt if not already encrypted
        if (!dataToStore.stripeCustomerId.startsWith('enc_')) {
          dataToStore.stripeCustomerId = encryptValue(
            dataToStore.stripeCustomerId,
            encryptionKey
          );
          debugLog('🔒 Encrypted stripeCustomerId for secure storage');
        }
      } else if (
        environment.strategy === 'MINIMAL_ENCRYPTION' &&
        encryptionKey
      ) {
        // Only encrypt if not already encrypted
        if (!dataToStore.stripeCustomerId.startsWith('enc_')) {
          try {
            dataToStore.stripeCustomerId = encryptValue(
              dataToStore.stripeCustomerId,
              encryptionKey
            );
            debugLog('🔒 Encrypted stripeCustomerId (minimal encryption mode)');
          } catch (e) {
            debugLog(
              '⚠️ Encryption failed, storing in plain text for OTO compatibility'
            );
          }
        }
      } else {
        // Plain text for mobile apps and restricted environments
        debugLog(
          '🔓 Storing stripeCustomerId in plain text for OTO compatibility:',
          environment.reason
        );
      }
    }

    // Write to all available storage mechanisms
    const dataStr = JSON.stringify(dataToStore);
    const writeResults = writeToStorage(USER_DATA_KEY, dataStr);

    // Clear performance cache since data changed
    clearPerformanceCache();

    return Promise.resolve();
  } catch (error) {
    debugError('Failed to store user data:', error);
    return Promise.reject(error);
  }
};

/**
 * Retrieve user data from all available storage mechanisms
 * Reads from storage mechanisms in priority order for maximum reliability
 * SECURITY: Hybrid encryption handling - decrypts if possible, preserves plain text for OTO compatibility
 * PERFORMANCE: Caches decrypted data for 30 seconds to avoid repeated decryption
 * WEBVIEW-SAFE: Automatically handles URL fallbacks for storage-restricted environments
 * @param {string} urlSearchParams - Optional URL search parameters override (for testing)
 * @returns {Object} User data object
 */
export const getUserData = (urlSearchParams = null) => {
  if (typeof window === 'undefined') return null;

  // Check cache first (only for decrypted data, not URL parameters)
  const now = Date.now();
  if (
    !urlSearchParams &&
    decryptionCache &&
    now - cacheTimestamp < CACHE_DURATION
  ) {
    return decryptionCache;
  }

  try {
    // Try to get stored data from any available mechanism
    const { value: storedDataStr, source } = readFromStorage(USER_DATA_KEY);

    if (storedDataStr) {
      const rawData = JSON.parse(storedDataStr);

      // Validate and repair data if needed
      const storedData = validateAndRepairUserData(rawData);

      if (storedData && Object.keys(storedData).length > 0) {
        // Handle customer ID based on whether it's encrypted or plain text
        const decryptedData = { ...storedData };
        const environment = detectStorageEnvironment();
        const encryptionKey = getEncryptionKey();

        // Handle stripeCustomerId - could be encrypted or plain text
        if (decryptedData.stripeCustomerId) {
          const isEncrypted = decryptedData.stripeCustomerId.startsWith('enc_');

          if (isEncrypted) {
            // Customer ID is encrypted, try to decrypt
            if (encryptionKey) {
              try {
                decryptedData.stripeCustomerId = decryptValue(
                  decryptedData.stripeCustomerId,
                  encryptionKey
                );
                debugLog('🔓 Successfully decrypted stripeCustomerId');
              } catch (decryptionError) {
                // Decryption failed - this shouldn't happen in hybrid mode
                debugLog(
                  '🚨 Decryption failed for encrypted stripeCustomerId:',
                  decryptionError.message
                );

                // In hybrid mode, if decryption fails, clear the customer ID but preserve other data
                // User will need to complete payment flow again
                decryptedData.stripeCustomerId = null;

                // Clean up corrupted data
                debugLog(
                  '⚠️ Corrupted stripeCustomerId removed - manual cleanup recommended'
                );
                clearPerformanceCache(); // Clear cache since data changed
              }
            } else {
              // No encryption key available for encrypted data
              debugLog(
                '🚨 Found encrypted stripeCustomerId but no encryption key available'
              );
              decryptedData.stripeCustomerId = null;

              // Clean up unusable encrypted data
              debugLog(
                '⚠️ Unusable encrypted stripeCustomerId removed - manual cleanup recommended'
              );
              clearPerformanceCache(); // Clear cache since data changed
            }
          } else {
            // Customer ID is plain text (mobile app or fallback mode)
            debugLog(
              '🔓 Found plain text stripeCustomerId (mobile app compatibility mode)'
            );
            // Keep as-is, no decryption needed
          }
        }

        // Cache the result if not using URL override
        if (!urlSearchParams) {
          decryptionCache = decryptedData;
          cacheTimestamp = now;
        }

        return decryptedData;
      }
    }

    // Fallback to URL parameters if no stored data found
    const searchParams =
      urlSearchParams ||
      (typeof window !== 'undefined' ? window.location.search : null);

    if (searchParams) {
      const urlData = extractFromUrlParams(searchParams);
      if (Object.keys(urlData).length > 0) {
        debugLog('📊 Retrieved user data from URL parameters:', {
          email: urlData.email ? `${urlData.email.substring(0, 3)}***` : 'none',
          fields: Object.keys(urlData),
          source: urlSearchParams
            ? 'provided_override'
            : 'automatic_window_location',
        });

        return urlData;
      }
    }

    return null;
  } catch (error) {
    debugError('Failed to retrieve user data:', error);
    // Clear caches without causing circular import
    clearPerformanceCache();
    try {
      removeFromStorage(USER_DATA_KEY);
    } catch (e) {
      debugError('Failed to clear user data on error:', e);
    }
    return null;
  }
};

/**
 * Clear all stored user data from all storage mechanisms
 * SECURITY: Also clears encryption keys (all storage types)
 */
export const clearUserData = () => {
  if (typeof window === 'undefined') return;

  try {
    removeFromStorage(USER_DATA_KEY);

    // Clear performance cache
    clearPerformanceCache();

    debugLog('🗑️ All user data cleared from all storage mechanisms');
  } catch (error) {
    debugError('Failed to clear user data:', error);
  }
};

/**
 * Remove specific fields from user data across all storage mechanisms
 * SECURITY: Handles hybrid encryption strategy properly
 * @param {string|string[]} fieldsToRemove - Field name or array of field names to remove
 */
export const removeUserDataFields = async fieldsToRemove => {
  if (typeof window === 'undefined') return Promise.resolve();

  // Handle null/undefined input gracefully
  if (!fieldsToRemove) return Promise.resolve();

  const fieldArray = Array.isArray(fieldsToRemove)
    ? fieldsToRemove
    : [fieldsToRemove];

  try {
    const { value: existingDataStr } = readFromStorage(USER_DATA_KEY);
    if (!existingDataStr) return Promise.resolve();

    const existingData = JSON.parse(existingDataStr);

    // Remove the specified fields
    fieldArray.forEach(field => {
      delete existingData[field];
    });

    // If no fields left, clear everything
    if (Object.keys(existingData).length === 0) {
      clearUserData();
      return Promise.resolve();
    }

    // Re-store the remaining data using current environment strategy
    const cleanData = { ...existingData };

    // If customer ID exists and was encrypted, decrypt it first so storeUserData can re-apply strategy
    if (
      cleanData.stripeCustomerId &&
      cleanData.stripeCustomerId.startsWith('enc_')
    ) {
      const encryptionKey = getEncryptionKey();
      if (encryptionKey) {
        try {
          cleanData.stripeCustomerId = decryptValue(
            cleanData.stripeCustomerId,
            encryptionKey
          );
        } catch (e) {
          // If decryption fails, remove the corrupted customer ID
          delete cleanData.stripeCustomerId;
        }
      } else {
        // No key available, remove encrypted data
        delete cleanData.stripeCustomerId;
      }
    }

    // Clear ALL storage first to prevent data merging
    removeFromStorage(USER_DATA_KEY);

    // Re-store with current environment strategy
    await storeUserData(cleanData);

    // Clear the decryption cache
    clearPerformanceCache();

    debugLog(
      `🗑️ Removed fields across all mechanisms: ${fieldArray.join(', ')}`
    );
    return Promise.resolve();
  } catch (error) {
    debugError('Failed to remove user data fields:', error);
    return Promise.reject(error);
  }
};

/**
 * Get hashed email for Meta Pixel Advanced Matching
 * Uses the new multi-storage getUserData function
 */
export const getHashedUserData = async (userData = null) => {
  const data = userData || getUserData();
  if (!data || !data.email) return {};

  const hashedData = {};

  // Hash email if available
  if (data.email) {
    hashedData.em = await hashData(data.email);
  }

  return hashedData;
};

/**
 * Hash data using SHA-256 (same as server-side)
 */
const hashData = async data => {
  if (!data) return null;

  // Normalize data (lowercase, trim)
  const normalizedData = data.toLowerCase().trim();

  // Use Web Crypto API if available (modern browsers)
  if (typeof window !== 'undefined' && window.crypto && window.crypto.subtle) {
    try {
      const encoder = new TextEncoder();
      const dataBuffer = encoder.encode(normalizedData);
      const hashBuffer = await window.crypto.subtle.digest(
        'SHA-256',
        dataBuffer
      );
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    } catch (error) {
      debugWarn('Web Crypto API failed, using fallback hash:', error);
    }
  }

  // Fallback: simple hash (not cryptographically secure, but consistent)
  let hash = 0;
  for (let i = 0; i < normalizedData.length; i++) {
    const char = normalizedData.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash).toString(16);
};
